import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileDown, FileSpreadsheet, Loader2, FileText } from "lucide-react";
import moment from "moment";
import jsPDF from "jspdf";
import "moment/locale/ca";

moment.locale("ca");

const TYPE_LABELS = { entry: "Entrada", exit: "Sortida", pause_start: "Inici pausa", pause_end: "Fi pausa" };

function calcWorkedMinutes(records) {
  const sorted = [...records].filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let total = 0, entryTime = null;
  for (const r of sorted) {
    if (r.type === "entry") entryTime = new Date(r.timestamp);
    else if (r.type === "exit" && entryTime) {
      const mins = (new Date(r.timestamp) - entryTime) / 60000;
      // Sanity check: max 24h per entry-exit pair
      if (mins > 0 && mins <= 1440) total += mins;
      entryTime = null;
    }
  }
  // Do NOT accumulate open entries (no exit) — avoids phantom hours
  return Math.max(0, Math.round(total));
}

function minutesToHHMM(mins) {
  if (!mins) return "0h 00m";
  return `${Math.floor(mins / 60)}h ${(mins % 60).toString().padStart(2, "0")}m`;
}

export default function TimeExport({ venueId, venueName, members }) {
  const [dateFrom, setDateFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [selectedEmail, setSelectedEmail] = useState("all");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);

  // Jornada PDF state
  const [jornadaMonth, setJornadaMonth] = useState(moment().format("YYYY-MM"));
  const [jornadaMember, setJornadaMember] = useState("");
  const [loadingJornada, setLoadingJornada] = useState(false);

  const fetchRecords = async () => {
    const query = {
      venue_id: venueId,
      date_from: dateFrom,
      date_to: dateTo,
      limit: 2000,
    };
    if (selectedEmail !== "all") query.user_email = selectedEmail;
    const all = await hosys.entities.TimeRecord.filter(query);
    // IMPORTANT: include historical records from workers no longer in `members`
    // (i.e., ex-employees). Only exclude soft-deleted records. Their hours must
    // remain in the past-period reports for legal compliance.
    return all.filter(r => !r.is_deleted)
      .sort((a, b) => a.date.localeCompare(b.date) || new Date(a.timestamp) - new Date(b.timestamp));
  };

  // Build a comprehensive member lookup that includes ex-employees.
  // Falls back to email (or record's user_name if available) when unknown.
  const buildMemberLookup = (records) => {
    const map = {};
    members.forEach(m => {
      map[m.user_email] = { name: m.user_name || m.user_email, dni_nie: m.dni_nie || "" };
    });
    // For emails present in records but missing from members (ex-workers),
    // use the name stored on the record itself
    records.forEach(r => {
      if (!map[r.user_email]) {
        map[r.user_email] = { name: r.user_name || r.user_email, dni_nie: "" };
      }
    });
    return map;
  };

  const exportPDF = async () => {
    setLoadingPdf(true);
    const records = await fetchRecords();
    // Build member lookup map: includes ex-employees with hours in the period
    const memberMap = buildMemberLookup(records);

    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(123, 45, 63);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("REGISTRE DE JORNADA LABORAL", pageW / 2, 12, { align: "center" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`${venueName} · ${dateFrom} / ${dateTo}`, pageW / 2, 20, { align: "center" });
    if (selectedEmail !== "all") {
      const m = memberMap[selectedEmail];
      const workerLabel = m ? `${m.name}${m.dni_nie ? ` · DNI/NIE: ${m.dni_nie}` : ""}` : selectedEmail;
      doc.text(`Treballador/a: ${workerLabel}`, pageW / 2, 25, { align: "center" });
    }

    // Legal notice
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(7);
    doc.text("Document generat conforme al Reial Decret-llei 8/2019 de mesures urgents de protecció social i de lluita contra la precarietat laboral.", 14, 35, { maxWidth: pageW - 28 });

    // Group by worker then by date
    const byWorker = {};
    records.forEach(r => {
      if (!byWorker[r.user_email]) {
        const info = memberMap[r.user_email] || { name: r.user_name || r.user_email, dni_nie: "" };
        byWorker[r.user_email] = { name: info.name, dni_nie: info.dni_nie, records: [] };
      }
      byWorker[r.user_email].records.push(r);
    });

    let y = 44;
    Object.values(byWorker).forEach(({ name, dni_nie, records: workerRecs }) => {
      // Worker header
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFillColor(245, 246, 250);
      doc.rect(14, y, pageW - 28, 8, "F");
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const workerHeader = dni_nie ? `${name}  ·  DNI/NIE: ${dni_nie}` : name;
      doc.text(workerHeader, 16, y + 5.5);

      const byDate = {};
      workerRecs.forEach(r => { if (!byDate[r.date]) byDate[r.date] = []; byDate[r.date].push(r); });
      const totalMins = Object.values(byDate).reduce((acc, d) => acc + calcWorkedMinutes(d), 0);
      doc.setFontSize(8);
      doc.text(`Total: ${minutesToHHMM(totalMins)}`, pageW - 14, y + 5.5, { align: "right" });
      y += 10;

      // Table header
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text("Data", 16, y);
      doc.text("Tipus", 60, y);
      doc.text("Hora", 110, y);
      doc.text("Hores jornada", 145, y);
      doc.line(14, y + 1.5, pageW - 14, y + 1.5);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      Object.keys(byDate).sort().forEach(date => {
        const dayRecs = byDate[date].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        const worked = calcWorkedMinutes(dayRecs);
        // Skip orphaned records (only exits without entries, or vice versa) that produce 0h
        const hasEntry = dayRecs.some(r => r.type === "entry");
        const hasExit = dayRecs.some(r => r.type === "exit");
        if (worked === 0 && !(hasEntry && hasExit)) return;
        let isFirstRow = true;
        dayRecs.forEach(r => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.setFontSize(8);
          doc.text(isFirstRow ? moment(date).format("DD/MM/YYYY") : "", 16, y);
          doc.text(TYPE_LABELS[r.type] || r.type, 60, y);
          doc.text(moment(r.timestamp).format("HH:mm"), 110, y);
          if (isFirstRow) doc.text(minutesToHHMM(worked), 145, y);
          y += 5;
          isFirstRow = false;
        });
        y += 2;
      });
      y += 4;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Generat: ${moment().format("DD/MM/YYYY HH:mm")} · Pàgina ${i}/${pageCount}`, pageW / 2, 290, { align: "center" });
    }

    doc.save(`registre-jornada-${dateFrom}-${dateTo}.pdf`);
    setLoadingPdf(false);
  };

  const exportCSV = () => {
    setLoadingExcel(true);
    fetchRecords().then(records => {
      // Format d'inspecció oficial: sense traces d'intervenció administrativa
      const headers = ["Treballador", "DNI/NIE", "Email", "Data", "Tipus", "Hora"];
      const memberMap = buildMemberLookup(records);
      const rows = records.map(r => {
        const info = memberMap[r.user_email] || { name: r.user_name || r.user_email, dni_nie: "" };
        return [
          info.name,
          info.dni_nie,
          r.user_email,
          r.date,
          TYPE_LABELS[r.type] || r.type,
          moment(r.timestamp).format("HH:mm"),
        ];
      });
      const csv = [headers, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registre-jornada-${dateFrom}-${dateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setLoadingExcel(false);
    });
  };

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <p className="text-sm text-blue-800 font-medium">Exportació per inspeccions laborals</p>
        <p className="text-xs text-blue-600 mt-0.5">Genera el registre oficial conforme al RD-Llei 8/2019. Inclou tots els fitxatges amb hora exacta.</p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1 block">Des de</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1 block">Fins a</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} max={moment().format("YYYY-MM-DD")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1 block">Treballador</label>
          <select
            value={selectedEmail}
            onChange={e => setSelectedEmail(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tots els treballadors</option>
            {members.map(m => (
              <option key={m.user_email} value={m.user_email}>{m.user_name || m.user_email}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Export buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={exportPDF}
          disabled={loadingPdf}
          className="flex-1 bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white gap-2"
        >
          {loadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          Exportar PDF
        </Button>
        <Button
          onClick={exportCSV}
          disabled={loadingExcel}
          variant="outline"
          className="flex-1 gap-2 border-[#7B2D3F]/30 text-[#7B2D3F] hover:bg-[#7B2D3F]/5"
        >
          {loadingExcel ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          Exportar CSV/Excel
        </Button>
      </div>

      <div className="text-xs text-gray-400 text-center">
        Conservació legal: els registres es mantenen un mínim de 4 anys conforme a l'art. 21 del ET.
      </div>

      {/* Registro Diario de Jornada */}
      <div className="border-t border-gray-200 pt-5 mt-5">
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4">
          <p className="text-sm text-amber-800 font-medium">Registro diario de jornada</p>
          <p className="text-xs text-amber-600 mt-0.5">
            Document oficial mensual per treballador conforme als articles 12.4.c, 35.5 i 34.9 de l'Estatut dels Treballadors.
            Format amb torn de matí i tarda, signable.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Mes</label>
            <Input
              type="month"
              value={jornadaMonth}
              onChange={e => setJornadaMonth(e.target.value)}
              data-testid="jornada-month-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Treballador</label>
            <select
              value={jornadaMember}
              onChange={e => setJornadaMember(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="jornada-member-select"
            >
              <option value="">Selecciona un treballador</option>
              {members.map(m => (
                <option key={m.member_id || m.id} value={m.member_id || m.id}>
                  {m.user_name || m.user_email}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3">
          <Button
            onClick={async () => {
              if (!jornadaMember || !jornadaMonth) return;
              setLoadingJornada(true);
              try {
                const [y, m] = jornadaMonth.split("-");
                const apiBase = process.env.REACT_APP_BACKEND_URL || "";
                const url = `${apiBase}/api/attendance/jornada-pdf/${venueId}/${jornadaMember}?month=${parseInt(m)}&year=${parseInt(y)}`;
                const res = await fetch(url, { credentials: 'include' });
                if (!res.ok) throw new Error("Error generant PDF");
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                const member = members.find(mb => (mb.member_id || mb.id) === jornadaMember);
                a.download = `jornada_${(member?.user_name || "treballador").replace(/ /g, "_")}_${jornadaMonth}.pdf`;
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (e) {
                console.error(e);
              }
              setLoadingJornada(false);
            }}
            disabled={loadingJornada || !jornadaMember || !jornadaMonth}
            className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white gap-2"
            data-testid="jornada-download-btn"
          >
            {loadingJornada ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Descarregar Registro de Jornada
          </Button>
        </div>
      </div>
    </div>
  );
}